import { ApplicationRef, Injectable, inject } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { createEffect } from 'ngxtension/create-effect';
import { type Observable, catchError, concat, first, from, interval, map, of, switchMap } from 'rxjs';
import { OfflineReadinessStore } from './offline-readiness.store';

const UPDATE_CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;

/**
 * Keeps the app on the latest deployed build: polls the service worker for updates once the app is stable and then
 * every `UPDATE_CHECK_INTERVAL_MS`, so a new version is already downloaded and waiting by the time the tab next
 * navigates or reloads.
 *
 * Deliberately does NOT reload on `VERSION_READY`. A game is a long-lived session with unsaved local walkthrough
 * state, and yanking the tab out from under a player mid-turn to pick up a new build is worse than serving the
 * previous one a little longer. Only `unrecoverable` forces a reload, because at that point the current worker can
 * no longer serve the app at all.
 */
@Injectable({ providedIn: 'root' })
export class ServiceWorkerUpdateService {
    private readonly appRef = inject(ApplicationRef);

    private readonly swUpdate = inject(SwUpdate);

    private readonly offlineReadiness = inject(OfflineReadinessStore);

    private readonly checkForUpdate = createEffect<void>((trigger$: Observable<void>) => {
        return trigger$.pipe(
            switchMap(() => {
                this.offlineReadiness.checking();

                return from(this.swUpdate.checkForUpdate()).pipe(
                    map((hasUpdate) => {
                        if (!hasUpdate) {
                            this.offlineReadiness.ready();
                        }

                        return hasUpdate;
                    }),
                    catchError((err: unknown) => {
                        console.error('[service-worker] update check failed', err);
                        this.offlineReadiness.failed(err);

                        return of(false);
                    })
                );
            })
        );
    });

    /** Activates the downloaded update and reloads the app so the new version takes over. */
    async activateUpdate(): Promise<void> {
        if (!this.swUpdate.isEnabled) {
            return;
        }

        try {
            await this.swUpdate.activateUpdate();
        } finally {
            document.location.reload();
        }
    }

    constructor() {
        if (!this.swUpdate.isEnabled) {
            this.offlineReadiness.unavailable();

            return;
        }

        const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable));
        const onSchedule$ = concat(appIsStable$, interval(UPDATE_CHECK_INTERVAL_MS)).pipe(map(() => undefined));

        this.checkForUpdate(onSchedule$);

        this.swUpdate.versionUpdates.subscribe((event) => {
            switch (event.type) {
                case 'VERSION_DETECTED':
                    this.offlineReadiness.downloading();
                    break;
                case 'VERSION_READY':
                    this.offlineReadiness.updateReady();
                    break;
                case 'VERSION_INSTALLATION_FAILED':
                    this.offlineReadiness.failed(event.error);
                    break;
                case 'NO_NEW_VERSION_DETECTED':
                    this.offlineReadiness.ready();
                    break;
            }
        });

        // Deliberately unmanaged subscription: this service is root-provided and eagerly constructed once at
        // boot (see app.config), so its lifetime IS the app's - and the handler's page reload tears everything
        // down anyway. If this ever moves to a narrower scope it needs takeUntilDestroyed().
        this.swUpdate.unrecoverable.subscribe((event) => {
            this.offlineReadiness.failed(event.reason);
            document.location.reload();
        });
    }
}
